import React, { useMemo, useCallback } from 'react';
import { Tooltip } from 'antd';
import { Handle, Position } from 'reactflow';

// Custom node component with n8n-like styling
const CustomNode = React.memo(({ data, selected }) => {
  const module = data?.module;
  const settings = data?.settings;
  const isInput = data?.isInput || ['input', 'source'].includes(module?.type) || ['input', 'source'].includes(module?.module_type) || /input/i.test(module?.name || '');
  // New: separate display name & icon overrides
  const displayName = data?.displayName || module?.name;
  const icon = data?.icon || module?.image || '📦';
  
  // Check if this is a branching module
  const isBranchingModule = useMemo(() => {
    const moduleType = module?.type || module?.module_type || '';
    const moduleCategory = module?.category || '';
    const moduleName = module?.name || '';
    
    const isBranching = (
      (moduleType === 'processing' || moduleType === 'utility') &&
      (moduleCategory === 'task_management' || moduleName.includes('Status Check'))
    );
    
    // Debug logging
    if (moduleName && moduleName.includes('File Status')) {
      console.log('🔍 [CustomNode] Branching Check:', {
        name: moduleName,
        type: moduleType,
        category: moduleCategory,
        isBranching: isBranching,
        condition1: (moduleType === 'processing' || moduleType === 'utility'),
        condition2: (moduleCategory === 'task_management' || moduleName.includes('Status Check')),
        fullModule: module
      });
    }
    
    return isBranching;
  }, [module]);
  
  // Generate tooltip content
  const tooltipContent = useMemo(() => {
    if (!module || !module.fields) return displayName || (module ? module.name : data.label);

    const nodeSettings = settings || {};
    
    const formatValue = (value) => {
      if (value === null || value === undefined) return 'Not set';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };
    
    return (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{displayName}</div>
        {module.fields.map((field, index) => (
          <div key={index}>
            <strong>{field.label}:</strong> {formatValue(nodeSettings[field.name]) || formatValue(field.default_value) || 'Not set'}
          </div>
        ))}
      </div>
    );
  }, [module, displayName, data.label, settings]);

  // Context menu handler
  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    // We'll use the onNodeClick handler to set the selected node
    // The copy functionality will be handled by keyboard shortcuts
  }, []);

  // Get node color based on module type
  const getNodeColor = () => {
    if (!module) return '#f0f0f0';
    const type = module.type || module.module_type || '';
    switch (type.toLowerCase()) {
      case 'input':
      case 'source':
        return '#00d4aa'; // Teal for input nodes
      case 'output':
      case 'destination':
        return '#ff6b6b'; // Red for output nodes
      case 'processor':
      case 'transform':
        return '#4dabf7'; // Blue for processing nodes
      case 'condition':
      case 'logic':
        return '#ffd43b'; // Yellow for logic nodes
      case 'storage':
        return '#9775fa'; // Purple for storage nodes
      default:
        return '#868e96'; // Gray for unknown types
    }
  };

  const nodeColor = getNodeColor();

  return (
    <div
      onContextMenu={handleContextMenu}
      style={{
        background: '#fff',
        borderRadius: 10,
        minWidth: 150,
        minHeight: 60,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        boxShadow: selected 
          ? `0 4px 20px rgba(24, 144, 255, 0.3)` 
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        padding: '0'
      }}
    >
      {/* Color stripe on the left */}
      <div
        style={{
          width: 4,
          height: '100%',
          background: nodeColor,
          position: 'absolute',
          left: 0,
          top: 0
        }}
      />
      
      {!isInput && (
        <Handle 
          type="target" 
          position={Position.Top} 
          style={{ 
            background: nodeColor,
            border: 'none',
            width: 16,
            height: 8,
            top: -4,
            borderRadius: 2,
            zIndex: 10
          }}
          isConnectable={true}
        />
      )}
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        padding: '12px 16px',
        width: '100%',
        gap: 8
      }}>
        {/* Icon container */}
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: `${nodeColor}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          flexShrink: 0
        }}>
          <Tooltip title={tooltipContent} placement="top">
            <span>{icon}</span>
          </Tooltip>
        </div>
        
        {/* Node label - centered under icon */}
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          color: '#333',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          width: '100%'
        }}>
          {displayName}
        </div>
      </div>
      
      {/* Output handles - branching or standard */}
      {isBranchingModule ? (
        <>
          {/* True/Success Handle - Left */}
          <Handle 
            type="source" 
            position={Position.Bottom}
            id="true"
            style={{ 
              background: '#52c41a',
              border: '2px solid #fff',
              width: 14,
              height: 14,
              bottom: -7,
              left: '30%',
              transform: 'translateX(-50%)',
              borderRadius: '50%',
              zIndex: 10
            }}
            isConnectable={true}
          />
          {/* False/Error Handle - Right */}
          <Handle 
            type="source" 
            position={Position.Bottom}
            id="false"
            style={{ 
              background: '#ff4d4f',
              border: '2px solid #fff',
              width: 14,
              height: 14,
              bottom: -7,
              left: '70%',
              transform: 'translateX(-50%)',
              borderRadius: '50%',
              zIndex: 10
            }}
            isConnectable={true}
          />
          {/* Labels for handles */}
          <div style={{
            position: 'absolute',
            bottom: -24,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-around',
            fontSize: 10,
            fontWeight: 600,
            color: '#666'
          }}>
            <span style={{ color: '#52c41a' }}>✓</span>
            <span style={{ color: '#ff4d4f' }}>✗</span>
          </div>
        </>
      ) : (
        /* Standard single output handle */
        <Handle 
          type="source" 
          position={Position.Bottom} 
          style={{ 
            background: nodeColor,
            border: 'none',
            width: 12,
            height: 12,
            bottom: -6,
            borderRadius: '50%',
            zIndex: 10
          }}
          isConnectable={true}
        />
      )}
    </div>
  );
});

export default CustomNode;
